import type {
  PhaseCrewRead,
  PhaseEquipmentRead,
  ProjectPhase,
} from '@frontend/AppSettings/services/PipelineProjectService';
import { dayMs, dateOnly, MS_PER_DAY } from './phaseGanttModel';

/**
 * The readiness feed — Build R (plans/phase-equipment-and-schedule-lifecycle.md, decision (f)),
 * pure and desk-provable like `phaseGanttModel`.
 *
 * The no-nag doctrine STANDS on the assignment panels: "0 of 3 named" at rest is plain text,
 * never a warning. This model is the ONE sanctioned advisory, confined by its trigger — a phase
 * earns a line only when it is IMMINENT (effective start within IMMINENT_DAYS, or already
 * running) and not complete, AND a planned slot half is still short. Everything else is silence:
 * a phase 30 days out with 0 of 9 named produces NOTHING here, and the strip renders nothing.
 *
 * Coverage counts are CAPPED per slot (min(named-in-slot, planned)): an over-filled Welder slot
 * never masks an empty Foreman slot, and a slot-less "parked" assignment fills no planned slot.
 * A phase that plans no labour (or no equipment) omits that half — never "0 of 0".
 *
 * Coverage counts REAL hands and live iron: a person removed from the matter team (`on_team`
 * false) or a retired unit (`unit_active` false) does not fill a slot here. This deliberately
 * DIVERGES from the panels' per-slot counts — an assignment surface lists the stranded row and
 * discloses it; an advisory about imminence must not read "covered" off a hand that is gone
 * (review M1).
 *
 * Unit double-booking is disclosed HERE and nowhere else (decision (b) deferred it to real
 * dates): two phases whose EFFECTIVE windows genuinely overlap while sharing an assigned unit —
 * LIVE iron only (review F12): a retired unit can work neither phase, so it triggers no clash,
 * the same live-hands rule coverage applies. Edge semantics matter (review H1): conversion
 * stamps each phase to START where its predecessor ENDS — the SAME calendar date
 * (`start_offset = end_offset`) — so a planned `planned_end` is an EXCLUSIVE boundary and
 * adjacent planned windows sharing it are abutment, never conflict. An entered `actual_end` is
 * the last day worked — INCLUSIVE. An OPEN phase (entered start, no entered end) is being worked
 * THROUGH TODAY however far past its planned end (review F5, mirroring the Gantt's actual bar) —
 * overrun is the clash's core case. The comparison converts everything to exclusive ends before
 * testing, so at rest (planned everywhere) this NEVER fires, and a genuinely shared working day
 * does. An advisory, never a wall — nothing here blocks any write.
 *
 * Imminence is bounded on BOTH sides (review F6): a phase whose effective window has fully
 * passed with no entered end is history, not imminence — without this bound every legacy
 * started-never-completed phase (sheets exist, 0028's actual_end never entered) would nag and
 * fetch forever, breaking the at-rest promises for exactly the projects already live. An OPEN
 * phase's window extends through today (F5), so a genuinely running overrun still shows.
 */

/** Display-only constant (decision (f)): how close an unstarted phase must be to earn a line. */
export const IMMINENT_DAYS = 7;

export interface ReadinessCoverage {
  named: number;
  planned: number;
}

export interface ReadinessRow {
  phaseId: string;
  phaseName: string;
  /** The phase's effective start (actual if entered, else planned) — why the line exists. */
  effectiveStart: string;
  /** True once the effective start is today or past (the running-phase variant of imminent). */
  started: boolean;
  /** null = that half plans no slots and is OMITTED — never rendered as "0 of 0". */
  labour: ReadinessCoverage | null;
  equipment: ReadinessCoverage | null;
}

export interface ReadinessOverlap {
  /** The unit's id — the collision-free render key (review F10: labels can repeat once asset
   * tags clear; names were never unique). */
  unitId: string;
  unitLabel: string;
  /** Display labels of the clashing phases — disambiguated when the project repeats a phase
   * name, so two overlapping "Bore"s read as two phases, not one (review F10). */
  phaseNames: string[];
}

export interface ReadinessModel {
  rows: ReadinessRow[];
  overlaps: ReadinessOverlap[];
}

const effectiveStartOf = (p: ProjectPhase): string | null =>
  dateOnly(p.actual_start) ?? dateOnly(p.planned_start);

/** The window as [startMs, endExclMs) for the overlap test. A planned end is ALREADY the
 * exclusive boundary (the successor starts on it — conversion's `start_offset = end_offset`);
 * an entered actual end is the last day WORKED, so exclusivity adds a day. An OPEN phase
 * (entered start, no entered end) runs THROUGH TODAY however far past its planned end — the
 * Gantt's actual bar semantics (review F5); a future-dated entered start has no worked days yet
 * and gets no window. */
const effectiveWindowOf = (
  p: ProjectPhase, todayMs: number,
): { startMs: number; endExclMs: number } | null => {
  const start = effectiveStartOf(p);
  if (!start) return null;
  const startMs = dayMs(start);
  const actualEnd = dateOnly(p.actual_end);
  const plannedEnd = dateOnly(p.planned_end);
  if (actualEnd) {
    // Guarded like the open branch below: an end WITHOUT a start (the PUT allows clearing one
    // half) falls back to planned_start, and a stray old end before it would mint an inverted
    // window — spuriously "overlapping" anything covering its start while F6 wrongly silenced
    // the phase. No window: the phase stays imminent by its dates alone (batch review M1).
    const endExclMs = dayMs(actualEnd) + MS_PER_DAY;
    return endExclMs > startMs ? { startMs, endExclMs } : null;
  }
  if (dateOnly(p.actual_start)) {
    const endExclMs = Math.max(plannedEnd ? dayMs(plannedEnd) : startMs, todayMs + MS_PER_DAY);
    return endExclMs > startMs ? { startMs, endExclMs } : null;
  }
  if (plannedEnd) return { startMs, endExclMs: dayMs(plannedEnd) };
  return null;
};

/** Imminent = not complete, dated, effectively starting within IMMINENT_DAYS (or started) — and
 * not yet fully PAST (review F6): a window that ended with no entered end is history, not
 * imminence, or every legacy unfinished phase would nag and fetch forever. A windowless-but-dated
 * phase (start entered, no end anywhere) stays imminent — nothing says it passed. */
const isImminent = (p: ProjectPhase, todayMs: number): boolean => {
  if (p.status === 'complete') return false;
  const start = effectiveStartOf(p);
  if (!start) return false;
  if (dayMs(start) > todayMs + IMMINENT_DAYS * MS_PER_DAY) return false;
  const window = effectiveWindowOf(p, todayMs);
  return window === null || window.endExclMs > todayMs;
};

/** The strip's FETCH gate: with nothing imminent there is nothing to reconcile, so the crew and
 * equipment reads are never issued — at-rest silence is a network promise too. */
export const anyImminent = (phases: ProjectPhase[], todayMs: number): boolean =>
  phases.some((p) => isImminent(p, todayMs));

/** Σ over slots of min(named-in-slot, planned) vs Σ planned — capped, so over-plan in one slot
 * never masks a short one. Slot-less assignments (key null) fill no planned slot. */
function coverage<S extends { project_phase_id: string; planned: number }>(
  phaseId: string,
  slots: S[],
  keyOf: (s: S) => string,
  assignedKeys: Map<string, number>,
): ReadinessCoverage | null {
  // planned > 0: a zero-quantity slot (a NULL-qty bid line copies as 0) plans nothing, so it can
  // neither be short nor mint the "0 of 0" decision (f) forbids (review L1).
  const mine = slots.filter((s) => s.project_phase_id === phaseId && s.planned > 0);
  if (mine.length === 0) return null;
  let named = 0;
  let planned = 0;
  mine.forEach((slot) => {
    const filled = assignedKeys.get(`${phaseId}|${keyOf(slot)}`) ?? 0;
    planned += slot.planned;
    named += Math.min(filled, slot.planned);
  });
  return { named, planned };
}

function countByPhaseAndKey<T extends { project_phase_id: string }>(
  rows: T[],
  keyOf: (r: T) => string | null,
  counts: (r: T) => boolean,
): Map<string, number> {
  const map = new Map<string, number>();
  rows.forEach((r) => {
    const key = keyOf(r);
    if (key === null) return;   // a parked assignment fills no planned slot
    if (!counts(r)) return;     // …and neither does a stranded one (review M1)
    const k = `${r.project_phase_id}|${key}`;
    map.set(k, (map.get(k) ?? 0) + 1);
  });
  return map;
}

/** Strict on the EXCLUSIVE ends: adjacent planned windows sharing their conversion-stamped
 * boundary date touch, they do not overlap; a shared actually-worked day does. */
const windowsOverlap = (
  a: { startMs: number; endExclMs: number }, b: { startMs: number; endExclMs: number },
): boolean => a.startMs < b.endExclMs && b.startMs < a.endExclMs;

function findOverlaps(
  phases: ProjectPhase[],
  equipment: PhaseEquipmentRead | undefined,
  todayMs: number,
): ReadinessOverlap[] {
  if (!equipment) return [];
  const byId = new Map(phases.map((p) => [p.id, p]));
  // Names have NO uniqueness constraint anywhere (the workbook inserts them verbatim), so a
  // repeated name is disambiguated by its 1-based sequence — two overlapping "Bore"s must read
  // as two phases, never collapse into one (review F10).
  const nameCounts = new Map<string, number>();
  phases.forEach((p) => nameCounts.set(p.name, (nameCounts.get(p.name) ?? 0) + 1));
  const displayName = (p: ProjectPhase): string =>
    ((nameCounts.get(p.name) ?? 0) > 1 ? `${p.name} (phase ${p.sequence + 1})` : p.name);
  const byUnit = new Map<string, { label: string; phaseIds: Set<string> }>();
  // Live iron only (review F12): a retired unit can work neither phase, so it clashes nowhere —
  // the same predicate coverage applies.
  equipment.assignments.filter((a) => a.unit_active).forEach((a) => {
    const entry = byUnit.get(a.inventory_item_id)
      ?? { label: a.asset_tag ? `${a.name ?? a.asset_tag} (${a.asset_tag})` : (a.name ?? a.inventory_item_id),
           phaseIds: new Set<string>() };
    entry.phaseIds.add(a.project_phase_id);
    byUnit.set(a.inventory_item_id, entry);
  });
  const out: ReadinessOverlap[] = [];
  byUnit.forEach(({ label, phaseIds }, unitId) => {
    const dated = [...phaseIds]
      .map((id) => byId.get(id))
      .filter((p): p is ProjectPhase => Boolean(p))
      .map((p) => ({ p, window: effectiveWindowOf(p, todayMs) }))
      .filter((w): w is { p: ProjectPhase; window: { startMs: number; endExclMs: number } } =>
        w.window !== null);
    const clashing = new Map<string, ProjectPhase>();
    for (let i = 0; i < dated.length; i += 1) {
      for (let j = i + 1; j < dated.length; j += 1) {
        if (windowsOverlap(dated[i].window, dated[j].window)) {
          clashing.set(dated[i].p.id, dated[i].p);
          clashing.set(dated[j].p.id, dated[j].p);
        }
      }
    }
    if (clashing.size > 0) {
      out.push({
        unitId,
        unitLabel: label,
        phaseNames: [...clashing.values()].map(displayName).sort(),
      });
    }
  });
  out.sort((a, b) => a.unitLabel.localeCompare(b.unitLabel) || a.unitId.localeCompare(b.unitId));
  return out;
}

export function buildReadiness(args: {
  phases: ProjectPhase[];
  crew?: PhaseCrewRead;
  equipment?: PhaseEquipmentRead;
  todayMs: number;
}): ReadinessModel {
  const { phases, crew, equipment, todayMs } = args;
  const crewCounts = crew
    ? countByPhaseAndKey(crew.assignments, (a) => a.role_key, (a) => a.on_team)
    : new Map<string, number>();
  const equipCounts = equipment
    ? countByPhaseAndKey(equipment.assignments, (a) => a.equipment_key, (a) => a.unit_active)
    : new Map<string, number>();

  const rows: ReadinessRow[] = [];
  [...phases].sort((a, b) => a.sequence - b.sequence).forEach((p) => {
    if (!isImminent(p, todayMs)) return;
    const labour = crew
      ? coverage(p.id, crew.slots, (s) => s.role_key, crewCounts)
      : null;
    const equip = equipment
      ? coverage(p.id, equipment.slots, (s) => s.equipment_key, equipCounts)
      : null;
    const labourShort = labour !== null && labour.named < labour.planned;
    const equipShort = equip !== null && equip.named < equip.planned;
    // Filled (or slot-less) phases earn NO line — the advisory disappears when filled.
    if (!labourShort && !equipShort) return;
    const start = effectiveStartOf(p) as string;
    rows.push({
      phaseId: p.id,
      phaseName: p.name,
      effectiveStart: start,
      started: dayMs(start) <= todayMs,
      labour,
      equipment: equip,
    });
  });

  return { rows, overlaps: findOverlaps(phases, equipment, todayMs) };
}
